import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';
import { generatePassword } from '../utils/generatePassword';
import { FaEye, FaEyeSlash, FaDice, FaCopy } from 'react-icons/fa';

const AddUsers = () => {
  const [userData, setUserData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    courier_id: '',
  });
  const [couriers, setCouriers] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false); // Toggle password visibility
  const [currentPage, setCurrentPage] = useState(1);
  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const apiUrl = process.env.REACT_APP_BASE_URL;

  // Fetch data: users, couriers, and roles
  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const [usersRes, couriersRes, rolesRes] = await Promise.all([
        fetch(`${apiUrl}/api/user?page=${page}`),
        fetch(`${apiUrl}/api/courier/all`),
        fetch(`${apiUrl}/api/roles`)
      ]);

      if (!usersRes.ok || !couriersRes.ok || !rolesRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const [usersData, couriersData, rolesData] = await Promise.all([
        usersRes.json(),
        couriersRes.json(),
        rolesRes.json()
      ]);

      console.log('Users:', usersData); // Log to check users
      console.log('Couriers:', couriersData); // Log to check couriers
      setUsers(usersData.data);
      setMeta({
        currentPage: usersData.meta.currentPage,
        totalPages: usersData.meta.totalPages,
        totalItems: usersData.meta.totalItems,
        itemsPerPage: usersData.meta.itemsPerPage
      });
      setCouriers(couriersData);
      setRoles(rolesData);
    } catch (error) {
      toast.error(error.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    console.log(`Field ${name} changed to ${value}`); // Add a log to track changes
    setUserData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };


  const handleGeneratePassword = () => {
    const newPassword = generatePassword(12); // Generate a 12-character password
    setUserData({ ...userData, password: newPassword });
  };

  const handleCopyPassword = () => {
    navigator.clipboard.writeText(userData.password);
    toast.success('Password copied to clipboard');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const method = editMode ? 'PUT' : 'POST';
      const endpoint = editMode
        ? `${apiUrl}/api/user/${currentUserId}`
        : `${apiUrl}/api/user/register`;

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('Failed to save user');
      }

      if (!editMode) await handleEmail(); // Send email after adding new user
      fetchData(currentPage);
      setModalOpen(false);
      setUserData({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        courier_id: '',
      });
    } catch (error) {
      toast.error(error.message || 'Error saving user');
    }
  };

  const handleEmail = async () => {
    const emailContent = `
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Welcome to AJO Shipping</title>
        <style>
          /* Your email styles here */
        </style>
      </head>
      <body>
        <div>
          <h1>Welcome to AJO Shipping!</h1>
          <p>Dear ${userData.first_name} ${userData.last_name},</p>
          <p>We’re excited to have you on board!</p>
          <h3>Your Account Details:</h3>
          <p>Email: ${userData.email}</p>
          <p>Password: ${userData.password}</p>
        </div>
      </body>
      </html>
    `;
    try {
      const response = await fetch(`${apiUrl}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: userData.email,
          subject: 'Welcome to AJO Shipping!',
          html: emailContent,
        }),
      });

      if (response.ok) {
        toast.success('Email sent successfully');
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      toast.error(error.message || 'Error sending email');
    }
  };

  const handleEdit = (id) => {
    const toEdit = users.find((user) => user.id === id);
    if (toEdit) {
      setUserData(toEdit);
      setCurrentUserId(id);
      setEditMode(true);
      setModalOpen(true);
    }
  };

  const handleDelete = async (id) => {
    try {
      await fetch(`${apiUrl}/api/user/${id}`, { method: 'DELETE' });
      fetchData(currentPage);
    } catch (error) {
      toast.error('Error deleting user');
    }
  };

  return (
    <Layout>
      <div className="p-4 bg-base-100 shadow-xl w-4/5 lg:w-full h-[100%]">
        <section className="flex justify-between items-center mb-4">
          <h2 className="text-3xl font-semibold">Users</h2>
          <button className="btn btn-error text-white" onClick={() => setModalOpen(true)}>
            Add User
          </button>
        </section>
        <section>
          <table className="table-auto w-full border-collapse border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border px-4 py-2">First Name</th>
                <th className="border px-4 py-2">Last Name</th>
                <th className="border px-4 py-2">Email</th>
                <th className="border px-4 py-2">Courier</th>
                <th className="border px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="border px-4 py-2">{user.first_name}</td>
                  <td className="border px-4 py-2">{user.last_name}</td>
                  <td className="border px-4 py-2">{user.email}</td>
                  <td className="border px-4 py-2">
                    {couriers.find((c) => c.id === user.courier_id)?.username || 'No Courier Selected'}

                  </td>
                  <td className="border px-4 py-2">
                    <button onClick={() => handleEdit(user.id)} className="btn btn-sm">
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      className="btn btn-sm btn-error"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <div className="mt-6">
          <Pagination
            currentPage={meta.currentPage}
            totalPages={meta.totalPages}
            totalItems={meta.totalItems}
            itemsPerPage={meta.itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </div>
      </div>

      {modalOpen && (
        <dialog
          className="modal modal-open fixed inset-0 flex items-center justify-center bg-black/50"
          onClick={(e) => {
            if (e.target.nodeName === 'DIALOG') {
              setModalOpen(false);
              setEditMode(false);
              setUserData({
                first_name: '',
                last_name: '',
                email: '',
                password: '',
                courier_id: '',
              });
            }
          }}
        >
          <form
            onSubmit={handleSubmit}
            className="modal-box bg-white p-6 rounded-lg shadow-lg w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-2xl font-bold text-gray-800 mb-4">
              {editMode ? 'Edit User' : 'Add User'}
            </h1>
            <div className="mb-4">
              <label className="block text-gray-600 mb-2">First Name</label>
              <input
                type="text"
                name="first_name"
                value={userData.first_name}
                onChange={handleChange}
                className="input input-bordered w-full"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-600 mb-2">Last Name</label>
              <input
                type="text"
                name="last_name"
                value={userData.last_name}
                onChange={handleChange}
                className="input input-bordered w-full"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-600 mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={userData.email}
                onChange={handleChange}
                className="input input-bordered w-full"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-600 mb-2">Password</label>
              <div className="relative w-full">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={userData.password}
                  onChange={handleChange}
                  className="input input-bordered w-full pr-12"
                  required={!editMode}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-600"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              {!editMode && (
                <div className="flex mt-2 gap-4">
                  <button type="button" onClick={handleGeneratePassword} className="btn">
                    <FaDice /> Generate
                  </button>
                  <button type="button" onClick={handleCopyPassword} className="btn">
                    <FaCopy /> Copy
                  </button>
                </div>
              )}
            </div>
            <div className="mb-6">
              <label className="block text-gray-600 mb-2">Courier</label>
              {loading ? <p>Loading couriers...</p> : (
                <select
                  name="courier_id"
                  value={userData.courier_id}
                  onChange={handleChange}
                  className="select select-bordered w-full"
                  required
                >
                  <option value="" disabled>
                    Select Courier
                  </option>
                  {couriers.map((courier) => (
                    <option key={courier.id} value={courier.id}>
                      {courier.username}
                    </option>
                  ))}
                </select>
              )}


            </div>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                className="btn bg-gray-300 hover:bg-gray-400 text-gray-800"
                onClick={() => {
                  setModalOpen(false);
                  setEditMode(false);
                  setUserData({
                    first_name: '',
                    last_name: '',
                    email: '',
                    password: '',
                    courier_id: '',
                  });

                }}
              >
                Cancel
              </button>
              <button type="submit" className="btn bg-blue-800 hover:bg-blue-600 text-white">
                {editMode ? 'Update' : 'Add'} User
              </button>
            </div>
          </form>
        </dialog>
      )}
    </Layout>
  );
};

export default AddUsers;
